import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Truck, Mail, Repeat } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { money, priceLine, productImage, productSrcSet, subPrice, subPct, shipFee, shipCountries, curCtx, SHORT_NAMES as NAMES, ZONE_SAMPLES } from "@/lib/api";
import { PRODUCT_COPY, FISH_FACTS, MULTI_FACTS, MULTI_LABEL, subTerms, SHIP_SHORT } from "@/data/content";
import { QtyStepper } from "@/components/CartDrawer";
import { SignupForm, LegalLinks } from "@/components/EmailPopup";
import { ClaimNote, Warnings } from "@/components/Wave";
import { Usd, AudNote, usePrice, UsdOnly } from "@/components/Usd";
import { TypeBadge } from "@/components/ProductBadge";
import { ServingInfo, LoyaltyLine, MemberLine, Spoonful } from "@/components/ProductExtras";
import { productAlt } from "@/data/seo";


const SoldOut = ({ p, sub = false }) => (
  <div data-testid={`soldout-block-${p.id}${sub ? "-subscribe" : ""}`} className="mt-4 sticker-sm bg-white p-5 md:p-6">
    <p className="font-display text-xl">Sold out for now</p>
    <p className="mt-2 text-sm text-slate-600">Get Grandpa's tips and we'll let you know when the {p.short_name} is back.</p>
    <div className="mt-4"><SignupForm source={`restock-${p.id}`} testPrefix={`restock-${p.id}${sub ? "-subscribe" : ""}`} cta="Sign me up" success="You're signed up. Grandpa will holler." /></div>
    <LegalLinks text="Unsubscribe anytime. See our" className="mt-3 text-slate-600" />
  </div>
);

export const productCountries = (p, settings) => shipCountries(settings).filter((c) => !p.ship_countries || p.ship_countries.includes(c));

export const AddToCart = ({ p, compact = false }) => {
  const { addToCart, cartCountry } = useStore();
  const price = usePrice();
  const usdHome = useContext(UsdOnly);
  const max = Math.min(20, p.available);
  const [qty, setQty] = useState(1);
  const blocked = p.ship_countries && !p.ship_countries.includes(cartCountry);
  if (blocked) return <p data-testid={`add-to-cart-blocked-${p.id}${compact ? "-sticky" : ""}`} className={`${compact ? "" : "mt-4"} rounded-xl border border-navy/15 bg-sand p-3 text-sm font-medium`}>US only. Your cart is set to {NAMES[cartCountry] || cartCountry}.</p>;
  return (
    <div className={compact ? "flex items-center gap-3" : "mt-4 flex flex-wrap items-center gap-4"}>
      {!compact && <QtyStepper value={qty} onChange={(n) => setQty(Math.max(1, Math.min(n, max)))} max={max} prefix={`quantity-${p.id}`} />}
      <button data-testid={`add-to-cart-${p.id}${compact ? "-sticky" : ""}`} disabled={p.available <= 0} onClick={() => { addToCart(p.id, qty); setQty(1); }} className={`btn-pop bg-sunny text-navy flex-1 ${usdHome ? "min-w-[200px] text-center leading-tight" : "min-w-[180px] whitespace-nowrap"}`}>
        {p.available <= 0 ? "Sold out" : `Add to cart · ${price(p.price_usd * qty)}`}
      </button>
    </div>
  );
};

const Option = ({ active, onClick, title, line, testId }) => (
  <button type="button" role="radio" aria-checked={active} data-testid={testId} onClick={onClick}
    className={`text-left rounded-2xl border p-4 transition-colors ${active ? "border-navy bg-navy text-sand" : "border-navy/15 bg-white hover:bg-foam"}`}>
    <span className="flex items-center gap-2 font-semibold"><span className={`h-4 w-4 rounded-full border-2 ${active ? "border-foam bg-foam" : "border-navy/40"}`} />{title}</span>
    <span className={`mt-1 block text-sm ${active ? "text-sand/80" : "text-slate-600"}`}>{line}</span>
  </button>
);

export const SubscribeBox = ({ p }) => {
  const { settings } = useStore();
  const sp = subPrice(p);
  const cs = productCountries(p, settings);
  const [agreed, setAgreed] = useState(false);
  return (
    <div className="mt-4">
      <div data-testid={`sub-terms-${p.id}`} className="sticker-sm bg-white p-4 text-sm space-y-1.5">
        <p className="font-semibold text-base">{money(sp)} per {p.kind === "stack" ? "stack" : "bottle"} + shipping, per delivery:</p>
        {ZONE_SAMPLES.filter(([c]) => c === "US" && cs.includes(c)).map(([c, label]) => <p key={c} data-testid={`sub-total-${p.id}-${c}`}>{label}: {money(sp)} + {money(shipFee(c, [p]))} = <b>{money(sp + shipFee(c, [p]))}</b></p>)}
        {cs.some((c) => c !== "US") && <p data-testid={`sub-intl-${p.id}`}>Worldwide shipping, priced by weight at checkout</p>}
        {subTerms(curCtx([p])).map((t) => <p key={t} className="flex gap-2 text-slate-700"><Repeat className="h-4 w-4 mt-0.5 shrink-0" />{t}</p>)}
      </div>
      {p.active && p.available > 0 ? (
        <>
          <label data-testid={`product-sub-consent-${p.id}`} className="mt-4 flex items-start gap-3 text-sm font-medium cursor-pointer">
            <input type="checkbox" required data-testid={`product-sub-consent-checkbox-${p.id}`} checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-navy" />
            <span>I agree my subscription renews automatically every 30 days at {money(sp)} + shipping, charged to my card until I cancel in My Account.</span>
          </label>
          {agreed
            ? <Link to={`/subscribe/${p.id}`} data-testid={`subscribe-button-${p.id}`} className="btn-pop bg-sunny text-navy w-full mt-4">{subPct(p) > 0 ? `Subscribe & save ${subPct(p)}%` : "Subscribe"}</Link>
            : <button type="button" disabled aria-disabled="true" data-testid={`subscribe-button-${p.id}`} className="btn-pop bg-sunny text-navy w-full mt-4">{subPct(p) > 0 ? `Subscribe & save ${subPct(p)}%` : "Subscribe"}</button>}
        </>
      ) : <SoldOut p={p} sub />}
    </div>
  );
};

export const PurchaseOptions = ({ p }) => {
  const { settings } = useStore();
  const [mode, setMode] = useState("once");
  const ship = p.ship_countries ? "+ shipping (US$15 US)" : SHIP_SHORT;
  return (
    <div className="mt-8" data-testid={`purchase-options-${p.id}`}>
      <div role="radiogroup" aria-label="Purchase option" className="grid sm:grid-cols-2 gap-3">
        <Option active={mode === "once"} onClick={() => setMode("once")} testId={`option-once-${p.id}`} title="One-time" line={<>{money(p.price_usd)} {ship}</>} />
        {!p.one_time_only && <Option active={mode === "sub"} onClick={() => setMode("sub")} testId={`option-subscribe-${p.id}`} title={subPct(p) > 0 ? `Subscribe & save ${subPct(p)}%` : "Subscribe (member discount n/a)"} line={<>{money(subPrice(p))} + shipping per delivery, every 30 days</>} />}
      </div>
      {mode === "once" ? (p.active && p.available > 0 ? <AddToCart p={p} /> : <SoldOut p={p} />) : <SubscribeBox p={p} />}
    </div>
  );
};

const Facts = ({ p }) => (p.id === "multi" ? <MultiLabel /> : (
  <dl data-testid={`product-facts-${p.id}`} className="mt-8 rounded-3xl bg-navy text-sand p-6 grid gap-4">
    {(p.id === "fish-oil" ? FISH_FACTS : MULTI_FACTS).map(([t, b]) => (
      <div key={t}><dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foam">{t}</dt>
        <dd data-testid={`product-${p.id}-${t.toLowerCase().replace(/ /g, "-")}`} className="mt-1 text-sand/90">{b}</dd></div>
    ))}
  </dl>
));

const Td = ({ children, className = "" }) => <td className={`py-1.5 align-top ${className}`}>{children}</td>;

const MultiLabel = () => {
  const L = MULTI_LABEL;
  return (
    <section data-testid="product-facts-multi" aria-labelledby="multi-facts-title" className="mt-8 rounded-3xl bg-white border-2 border-navy p-4 sm:p-5 text-navy">
      <h3 id="multi-facts-title" className="font-display text-3xl leading-none">Supplement Facts</h3>
      <p data-testid="multi-serving" className="mt-2 text-sm font-semibold border-b-4 border-navy pb-2">{L.serving} · {L.servings}</p>
      <table data-testid="multi-facts-table" className="w-full table-fixed border-collapse text-sm">
        <colgroup><col /><col className="w-[34%] sm:w-[30%]" /><col className="w-[17%] sm:w-[14%]" /></colgroup>
        <thead>
          <tr className="border-b-2 border-navy text-left text-xs font-bold">
            <th scope="col" className="py-1.5 pr-2">Nutrient</th>
            <th scope="col" className="py-1.5 pr-2">Amount Per Serving</th>
            <th scope="col" className="py-1.5 text-right">%DV</th>
          </tr>
        </thead>
        <tbody data-testid="multi-nutrients">
          {L.nutrients.map(([n, f, amt, dv]) => (
            <tr key={n} className="border-b border-navy/30">
              <th scope="row" className="py-1.5 pr-2 text-left align-top font-normal break-words"><b>{n}</b>{f && ` (${f})`}</th>
              <Td className="pr-2 break-words">{amt}</Td>
              <Td className="text-right font-bold">{dv}</Td>
            </tr>
          ))}
        </tbody>
        <tbody data-testid="multi-blends" className="border-t-4 border-navy">
          {L.blends.map(([n, amt, ing]) => (
            <tr key={n} className="border-b border-navy/30">
              <th scope="row" className="py-2 pr-2 text-left align-top font-normal break-words">
                <b>{n}</b>
                <span className="mt-1 block text-xs leading-snug">{ing}</span>
              </th>
              <Td className="py-2 pr-2">{amt}</Td>
              <Td className="py-2 text-right font-bold">**</Td>
            </tr>
          ))}
        </tbody>
      </table>
      <p data-testid="multi-dv-note" className="mt-1 text-xs font-semibold">{L.dvNote}</p>
      <p data-testid="multi-other" className="mt-3 text-sm border-t-4 border-navy pt-2"><b>Other Ingredients:</b> {L.other}</p>
      <p data-testid="multi-use" className="mt-2 text-sm"><b>Suggested Use:</b> {L.use}</p>
      <p data-testid="multi-caution" className="mt-2 text-sm"><b>Caution:</b> {L.caution}</p>
      <p data-testid="multi-warning" className="mt-2 text-sm"><b>Warning:</b> {L.warning}</p>
      <p data-testid="multi-fda" className="mt-2 text-xs">*These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.</p>
    </section>
  );
};

export const ProductPanel = ({ p, flip = false, featured = false, badge = false }) => {
  const { byId, settings } = useStore();
  const copy = PRODUCT_COPY[p.id] || { claims: [], warnings: () => "" };
  const ships = shipCountries(settings).map((c) => NAMES[c]).join(", ").replace(/, ([^,]*)$/, " and $1");
  return (
    <section id={p.id} data-testid={`product-panel-${p.id}`} className="container-gf py-12 md:py-16 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start scroll-mt-28">
      <div className={`lg:sticky lg:top-28 ${flip ? "lg:order-2" : ""}`}>
        <img src={productImage(p.id, 800)} srcSet={productSrcSet(p.id)} sizes="(max-width: 1024px) 100vw, 560px" alt={productAlt(p)} {...(!badge || featured ? { fetchpriority: "high" } : { loading: "lazy", decoding: "async" })} width="800" height="800" className="w-full aspect-square object-cover sticker" />
      </div>
      <div className="rise">
        {featured && <span data-testid={`featured-badge-${p.id}`} className="mr-2 inline-block rounded-full bg-sunny px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-navy">Featured</span>}
        {badge && <TypeBadge p={p} className="mr-2" />}
        <span className="label-chip">{p.count} {p.unit}</span>
        <h2 data-testid={`product-name-${p.id}`} className="mt-4 text-3xl md:text-5xl leading-[1.05]">{p.name}</h2>
        <p data-testid={`product-price-${p.id}`} className="mt-5 text-2xl font-semibold">{priceLine(p)}</p>
        <MemberLine p={p} className="mt-1 text-sm" />
        <ServingInfo p={p} />
        <p data-testid={`product-description-${p.id}`} className="mt-5 text-base md:text-lg text-slate-700">{p.description}</p>
        <ul className="mt-6 grid gap-2">
          {copy.claims.map((c) => <li key={c} className="flex items-start gap-2 text-sm font-medium"><Check className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={3} />{c}</li>)}
        </ul>
        <ClaimNote testId={`claim-disclaimer-${p.id}`} />
        <PurchaseOptions p={p} />
        <LoyaltyLine testId={`loyalty-line-${p.id}`} className="mt-4" />
        <Spoonful id={p.id} />
        <Facts p={p} />
        <div className="mt-6"><Warnings text={copy.warnings(p, byId)} testId={`warnings-block-${p.id}`} /></div>
        <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
          <div data-testid={`ships-to-${p.id}`} className="sticker-sm bg-white p-4 flex gap-3"><Truck className="h-5 w-5 shrink-0" /> Ships to {ships || "the US"}{shipCountries(settings).includes("US") ? " (no Alaska, Hawaii, US territories or military addresses)" : ""}.</div>
          <div className="sticker-sm bg-white p-4 flex gap-3"><Mail className="h-5 w-5 shrink-0" /> Questions? hello@grandpafinns.com</div>
        </div>
      </div>
    </section>
  );
};
